function withOpacityValue(variable: string) {
  return ({ opacityValue }: { opacityValue?: number }) => {
    if (opacityValue === undefined) {
      return `rgba(${variable})`;
    }
    return `rgba(${variable}, ${opacityValue})`;
  };
}

export const OCTOLANE_COLOR_TOKENS = {
  octolane: {
    brand: "rgb(var(--octolane-brand))",
    "brand-blue": "rgb(var(--octolane-brand-blue))",
    "brand-silver": "rgb(var(--octolane-brand-silver))",
    "brand-secondary": "rgb(var(--octolane-brand-secondary))",
    // Gray colors
    "gray-50": "rgb(var(--octolane-gray-50))",
    "gray-100": "rgb(var(--octolane-gray-100))",
    "gray-200": "rgb(var(--octolane-gray-200))",
    "gray-550": "rgb(var(--octolane-gray-550))",
    "gray-750": "rgb(var(--octolane-gray-750))",
  },
  text: {
    base: "rgb(var(--text-darker))",
    dark: "rgb(var(--text-dark))",
    darker: "rgb(var(--text-darker))",
    light: "rgb(var(--text-light))",
    lighter: "rgb(var(--text-lighter))",
    lightest: "rgb(var(--text-lightest))",
    white: "rgb(var(--text-white))",
  },
  border: {
    base: "rgb(var(--border-base))",
    dark: "rgb(var(--border-dark))",
    light: "var(--core-silver-a5)",
  },
  bg: {
    base: "rgb(var(--bg-base))",
    dark: "rgb(var(--bg-dark))",
    light: "rgb(var(--bg-light))",
    lighter: "rgb(var(--bg-lighter))",
    lightest: "rgb(var(--bg-lightest))",
    white: "rgb(var(--bg-base-12))",
    cream: "rgb(var(--bg-cream))",
    sidebar: "rgb(var(--bg-sidebar))",
    "sidebar-item-active": "rgb(var(--bg-sidebar-item-active))",
  },
  information: {
    base: "rgb(var(--information-base))",
    dark: "rgb(var(--information-dark))",
    light: "rgb(var(--information-light))",
    lighter: "rgb(var(--information-lighter))",
    lightest: "rgb(var(--information-lightest))",
  },
  error: {
    base: "rgb(var(--error-base))",
    dark: "rgb(var(--error-dark))",
    light: "rgb(var(--error-light))",
    lighter: "rgb(var(--error-lighter))",
  },
  success: {
    base: "rgb(var(--success-base))",
    dark: "rgb(var(--success-dark))",
    light: "rgb(var(--success-light))",
    lighter: "rgb(var(--success-lighter))",
  },
  warning: {
    base: "rgb(var(--warning-base))",
    dark: "rgb(var(--warning-dark))",
    light: "rgb(var(--warning-light))",
    lighter: "rgb(var(--warning-lighter))",
  },
  sky: {
    base: "rgb(var(--sky-base))",
    light: "rgb(var(--sky-light))",
    lighter: "rgb(var(--sky-lighter))",
  },
  yellow: {
    base: "rgb(var(--yellow-base))",
    dark: "rgb(var(--yellow-dark))",
    light: "rgb(var(--yellow-light))",
    lighter: "rgb(var(--yellow-lighter))",
  },
  purple: {
    base: "rgb(var(--purple-base))",
    dark: "rgb(var(--purple-dark))",
    light: "rgb(var(--purple-light))",
    lighter: "rgb(var(--purple-lighter))",
  },
  pink: {
    base: "rgb(var(--pink-base))",
    dark: "rgb(var(--pink-dark))",
    light: "rgb(var(--pink-light))",
    lighter: "rgb(var(--pink-lighter))",
  },
  teal: {
    base: "rgb(var(--teal-base))",
    dark: "rgb(var(--teal-dark))",
    light: "rgb(var(--teal-light))",
    lighter: "rgb(var(--teal-lighter))",
  },
  olive: {
    base: "rgb(var(--olive-base))",
    light: "rgb(var(--olive-light))",
    lighter: "rgb(var(--olive-lighter))",
  },
  slate: {
    base: "rgb(var(--slate-base))",
    light: "rgb(var(--slate-light))",
    lighter: "rgb(var(--slate-lighter))",
  },
};

export const OCTOLANE_DESIGN_TOKENS = {
  colors: {
    octolane: {
      brand: withOpacityValue("var(--octolane-brand)"),
      "brand-blue": withOpacityValue("var(--octolane-brand-blue)"),
      "brand-silver": withOpacityValue("var(--octolane-brand-silver)"),
      "brand-secondary": withOpacityValue("var(--octolane-brand-secondary)"),
      // Gray colors
      "gray-50": withOpacityValue("var(--octolane-gray-50)"),
      "gray-100": withOpacityValue("var(--octolane-gray-100)"),
      "gray-200": withOpacityValue("var(--octolane-gray-200)"),
      "gray-550": withOpacityValue("var(--octolane-gray-550)"),
      "gray-750": withOpacityValue("var(--octolane-gray-750)"),
    },
    text: {
      base: withOpacityValue("var(--text-darker)"),
      dark: withOpacityValue("var(--text-dark)"),
      darker: withOpacityValue("var(--text-darker)"),
      light: withOpacityValue("var(--text-light)"),
      lighter: withOpacityValue("var(--text-lighter)"),
      lightest: withOpacityValue("var(--text-lightest)"),
      white: withOpacityValue("var(--text-white)"),
    },
    border: {
      base: withOpacityValue("var(--border-base)"),
      dark: withOpacityValue("var(--border-dark)"),
      light: withOpacityValue("var(--border-core-silver-3)"),
    },
    bg: {
      base: withOpacityValue("var(--bg-base)"),
      dark: withOpacityValue("var(--bg-dark)"),
      light: withOpacityValue("var(--bg-light)"),
      lighter: withOpacityValue("var(--bg-lighter)"),
      lightest: withOpacityValue("var(--bg-lightest)"),
      white: withOpacityValue("var(--bg-base-12)"),
      cream: withOpacityValue("var(--bg-cream)"),
      sidebar: withOpacityValue("var(--bg-sidebar)"),
      "sidebar-item-active": withOpacityValue("var(--bg-sidebar-item-active)"),
    },
    information: {
      base: withOpacityValue("var(--information-base)"),
      dark: withOpacityValue("var(--information-dark)"),
      light: withOpacityValue("var(--information-light)"),
      lighter: withOpacityValue("var(--information-lighter)"),
      lightest: withOpacityValue("var(--information-lightest)"),
    },
    error: {
      base: withOpacityValue("var(--error-base)"),
      dark: withOpacityValue("var(--error-dark)"),
      light: withOpacityValue("var(--error-light)"),
      lighter: withOpacityValue("var(--error-lighter)"),
    },
    success: {
      base: withOpacityValue("var(--success-base)"),
      dark: withOpacityValue("var(--success-dark)"),
      light: withOpacityValue("var(--success-light)"),
      lighter: withOpacityValue("var(--success-lighter)"),
    },
    warning: {
      base: withOpacityValue("var(--warning-base)"),
      dark: withOpacityValue("var(--warning-dark)"),
      light: withOpacityValue("var(--warning-light)"),
      lighter: withOpacityValue("var(--warning-lighter)"),
    },
    sky: {
      base: withOpacityValue("var(--sky-base)"),
      light: withOpacityValue("var(--sky-light)"),
      lighter: withOpacityValue("var(--sky-lighter)"),
    },
    yellow: {
      base: withOpacityValue("var(--yellow-base)"),
      dark: withOpacityValue("var(--yellow-dark)"),
      light: withOpacityValue("var(--yellow-light)"),
      lighter: withOpacityValue("var(--yellow-lighter)"),
    },
    purple: {
      base: withOpacityValue("var(--purple-base)"),
      dark: withOpacityValue("var(--purple-dark)"),
      light: withOpacityValue("var(--purple-light)"),
      lighter: withOpacityValue("var(--purple-lighter)"),
    },
    pink: {
      base: withOpacityValue("var(--pink-base)"),
      dark: withOpacityValue("var(--pink-dark)"),
      light: withOpacityValue("var(--pink-light)"),
      lighter: withOpacityValue("var(--pink-lighter)"),
    },
    teal: {
      base: withOpacityValue("var(--teal-base)"),
      dark: withOpacityValue("var(--teal-dark)"),
      light: withOpacityValue("var(--teal-light)"),
      lighter: withOpacityValue("var(--teal-lighter)"),
    },
    olive: {
      base: withOpacityValue("var(--olive-base)"),
      light: withOpacityValue("var(--olive-light)"),
      lighter: withOpacityValue("var(--olive-lighter)"),
    },
    slate: {
      base: withOpacityValue("var(--slate-base)"),
      light: withOpacityValue("var(--slate-light)"),
      lighter: withOpacityValue("var(--slate-lighter)"),
    },
  },
  radius: {
    base: "var(--radius-base)",
    md: "var(--radius-md)",
    lg: "var(--radius-lg)",
    full: "var(--radius-full)",
  },
  shadows: {
    sm: "var(--shadow-sm)",
    md: "var(--shadow-md)",
    "white-bg": "var(--shadow-white-bg)",
  },
  padding: {
    base: "var(--padding-base)",
    md: "var(--padding-md)",
    lg: "var(--padding-lg)",
    xl: "var(--padding-xl)",
  },
  fontSize: {
    "body-xs": "var(--font-body-xs)",
    "body-sm": "var(--font-body-sm)",
    "body-md": "var(--font-body-md)",
    "body-lg": "var(--font-body-lg)",
    "body-xl": "var(--font-body-xl)",
    "title-h1": "var(--font-title-h1)",
    "title-h2": "var(--font-title-h2)",
    "title-h3": "var(--font-title-h3)",
    "title-h4": "var(--font-title-h4)",
    "title-h5": "var(--font-title-h5)",
    "title-h6": "var(--font-title-h6)",
  },
};
